/**
 * @fileoverview Componente responsável por confirmar a exclusão de filas.
 */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  makeStyles,
  IconButton,
  Paper,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Warning as WarningIcon, SwapHoriz as TransferIcon } from "@material-ui/icons";
import { X, Trash2 } from "lucide-react";
const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    background: theme.mode === 'dark'
      ? 'linear-gradient(135deg, rgba(18, 18, 19, 0.95) 0%, rgba(22, 22, 24, 0.95) 100%)'
      : '#FFFFFF',
    border: theme.mode === 'dark' ? '1px solid rgba(91, 124, 255, 0.2)' : 'none',
    borderRadius: "20px",
    boxShadow: theme.mode === 'dark'
      ? '0 20px 60px rgba(0, 0, 0, 0.5)'
      : '0 10px 40px rgba(0, 0, 0, 0.1)',
  },
  dialogTitle: {
    padding: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: theme.mode === 'dark'
      ? '1px solid rgba(91, 124, 255, 0.1)'
      : '1px solid rgba(0, 0, 0, 0.08)',
  },
  titleContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  titleIcon: {
    fontSize: '2rem',
    color: theme.palette.error.main,
  },
  titleText: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: theme.mode === 'dark' ? '#FFFFFF' : '#1e293b',
  },
  closeButton: {
    color: theme.mode === 'dark' ? '#94a3b8' : '#64748b',
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  warningBox: {
    background: theme.mode === 'dark'
      ? 'rgba(239, 68, 68, 0.1)'
      : 'rgba(239, 68, 68, 0.05)',
    border: `2px solid ${theme.palette.error.main}`,
    borderRadius: '12px',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  warningText: {
    color: theme.mode === 'dark' ? '#FFFFFF' : '#1e293b',
    fontWeight: 600,
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.mode === 'dark' ? '#FFFFFF' : '#1e293b',
    marginBottom: theme.spacing(1.5),
  },
  radioGroup: {
    gap: theme.spacing(1),
  },
  radioOption: {
    background: theme.mode === 'dark'
      ? 'rgba(91, 124, 255, 0.05)'
      : 'rgba(61, 92, 253, 0.03)',
    border: theme.mode === 'dark'
      ? '1px solid rgba(91, 124, 255, 0.2)'
      : '1px solid rgba(0, 0, 0, 0.08)',
    borderRadius: '12px',
    padding: theme.spacing(1.5),
    margin: 0,
    transition: 'all 0.2s',
    '&:hover': {
      background: theme.mode === 'dark'
        ? 'rgba(91, 124, 255, 0.1)'
        : 'rgba(61, 92, 253, 0.08)',
    },
  },
  radioLabel: {
    fontSize: '0.95rem',
    fontWeight: 500,
    color: theme.mode === 'dark' ? '#FFFFFF' : '#1e293b',
  },
  formControl: {
    width: '100%',
  },
  select: {
    background: theme.mode === 'dark'
      ? 'rgba(91, 124, 255, 0.05)'
      : '#FFFFFF',
    borderRadius: '12px',
    '& .MuiOutlinedInput-notchedOutline': {
      border: theme.mode === 'dark'
        ? '1px solid rgba(91, 124, 255, 0.2)'
        : '1px solid rgba(0, 0, 0, 0.12)',
    },
  },
  dialogActions: {
    padding: theme.spacing(3),
    borderTop: theme.mode === 'dark'
      ? '1px solid rgba(91, 124, 255, 0.1)'
      : '1px solid rgba(0, 0, 0, 0.08)',
    gap: theme.spacing(2),
  },
  cancelButton: {
    borderRadius: '12px',
    textTransform: 'none',
    fontWeight: 600,
    padding: '10px 24px',
    color: theme.mode === 'dark' ? '#94a3b8' : '#64748b',
  },
  deleteButton: {
    borderRadius: '12px',
    textTransform: 'none',
    fontWeight: 600,
    padding: '10px 24px',
    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    color: '#FFFFFF',
    '&:hover': {
      background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
    },
  },
}));

const DeleteQueueModal = ({
  open,
  onClose,
  queue,
  queues,
  onConfirm,
  ticketsCount,
}) => {
  const classes = useStyles();
  const [transferOption, setTransferOption] = useState("no"); // "yes" ou "no"
  const [targetQueueId, setTargetQueueId] = useState("");

  // Resetar ao abrir
  useEffect(() => {
    if (open) {
      setTransferOption("no");
      setTargetQueueId("");
    }
  }, [open]);

  const availableQueues = queues.filter(q => q.id !== queue?.id);

  const handleConfirm = () => {
    if (transferOption === "yes" && !targetQueueId) {
      return; // Não permite confirmar sem selecionar fila de destino
    }

    onConfirm(transferOption === "yes" ? targetQueueId : null);
  };

  const canConfirm = transferOption === "no" || (transferOption === "yes" && targetQueueId);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <div className={classes.titleContent}>
          <Trash2 className={classes.titleIcon} />
          <Typography className={classes.titleText}>
            Excluir Fila
          </Typography>
        </div>
        <IconButton
          onClick={onClose}
          className={classes.closeButton}
          size="small"
        >
          <X />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {ticketsCount > 0 && (
          <Box className={classes.warningBox}>
            <WarningIcon style={{ color: '#ef4444', fontSize: '2rem' }} />
            <Box>
              <Typography className={classes.warningText}>
                Esta fila possui {ticketsCount} ticket(s) associado(s)
              </Typography>
              <Typography variant="body2" style={{ marginTop: 4 }}>
                Você precisa decidir o que fazer com esses tickets
              </Typography>
            </Box>
          </Box>
        )}

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            Você tem certeza que deseja excluir a fila "{queue?.name}"?
          </Typography>
        </Box>

        {ticketsCount > 0 && (
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>
              Deseja transferir os tickets para outra fila?
            </Typography>
            <RadioGroup
              value={transferOption}
              onChange={(e) => setTransferOption(e.target.value)}
              className={classes.radioGroup}
            >
              <FormControlLabel
                value="no"
                control={<Radio color="primary" />}
                label={
                  <Box>
                    <Typography className={classes.radioLabel}>
                      Não, excluir mesmo assim
                    </Typography>
                    <Typography variant="caption" style={{ color: '#94a3b8' }}>
                      Os tickets ficarão sem fila associada
                    </Typography>
                  </Box>
                }
                className={classes.radioOption}
              />
              <FormControlLabel
                value="yes"
                control={<Radio color="primary" />}
                label={
                  <Box>
                    <Typography className={classes.radioLabel}>
                      Sim, transferir para outra fila
                    </Typography>
                    <Typography variant="caption" style={{ color: '#94a3b8' }}>
                      Escolha a fila de destino abaixo
                    </Typography>
                  </Box>
                }
                className={classes.radioOption}
              />
            </RadioGroup>

            {transferOption === "yes" && (
              <Box mt={2}>
                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  size="small"
                >
                  <InputLabel>Fila de Destino</InputLabel>
                  <Select
                    value={targetQueueId}
                    onChange={(e) => setTargetQueueId(e.target.value)}
                    label="Fila de Destino"
                    className={classes.select}
                  >
                    {availableQueues.map((q) => (
                      <MenuItem key={q.id} value={q.id}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            style={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: q.color,
                            }}
                          />
                          {q.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>
        )}

        {ticketsCount === 0 && (
          <Alert severity="info">
            Esta fila não possui tickets associados e pode ser excluída sem problemas.
          </Alert>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={onClose}
          className={classes.cancelButton}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          className={classes.deleteButton}
          disabled={!canConfirm}
          startIcon={<Trash2 />}
        >
          Excluir Fila
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteQueueModal;
